import { logger } from '@/lib/logger';
import { Browser, browserName } from '@helpers/getBrowserDetails';
import {
  LAST_AUDIO_INPUT_ID,
  LAST_AUDIO_OUTPUT_ID,
  LAST_VIDEO_INPUT_ID,
  LOCAL_AUDIO_MUTED,
  LOCAL_VIDEO_MUTED,
} from '@src/const/localStorageConsts';
import { getRouter } from '@/const/routerHandle';
import { CheckValue } from '@src/const/videoCallTestConsts';
import { useAuthStore } from '@stores/authStore';
import { useCallTestStore } from '@stores/call/callTestStore';
import { P2pDataType, P2pSystemMsg } from '@stores/call/callTypes';
import { useDataConnectionsStore } from '@stores/call/dataConnectionsStore';
import { useMediaConnectionsStore } from '@stores/call/mediaConnectionsStore';
import { CALL_HARD_ERRORS } from '@stores/call/callNotices';
import { useModalStore } from '@stores/modalStore';
import { getPath } from '@therame/shared';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

const supportsAudioOutput = 'setSinkId' in HTMLMediaElement.prototype;

export const useLocalMediaStore = defineStore('localMedia', () => {
  const authStore = useAuthStore();
  const modalStore = useModalStore();
  const dataConnectionsStore = useDataConnectionsStore();
  const mediaConnectionsStore = useMediaConnectionsStore();
  const callTestStore = useCallTestStore();

  const localStream = ref<MediaStream | undefined>(undefined);
  /**
   * Whether the last attempt to acquire camera/mic was refused or failed.
   *
   * Real state rather than `modalStore.isModalOpen('streamError')`, which the
   * settings modal used to read: that made the inline "kein Zugriff" hint
   * disappear the moment the dialogue was dismissed, even though the camera was
   * still unavailable — and the settings modal is exactly where the user goes to
   * fix it.
   */
  const mediaAccessFailed = ref<boolean>(false);
  const localAudioMuted = ref<boolean>(false);
  const localVideoMuted = ref<boolean>(false);
  const inputConstraints = ref<MediaStreamConstraints>({ audio: true, video: true });
  const selectedVideoInputId = ref<string>('default');
  const selectedAudioInputId = ref<string>('default');
  const selectedAudioOutputId = ref<string>('');
  const screenShareEnabled = ref<boolean>(false);
  /** kept so stopping the share can release the capture without depending on the cam */
  const screenShareStream = ref<MediaStream | undefined>(undefined);
  const videoDevices = ref<MediaDeviceInfo[]>([]);
  const audioInputs = ref<MediaDeviceInfo[]>([]);
  const audioOutputs = ref<MediaDeviceInfo[]>([]);
  const streamInitialized = ref<boolean>(false);
  const globalAudioOutputMuted = ref<boolean>(false);

  const toggleOutputMute = () => {
    globalAudioOutputMuted.value = !globalAudioOutputMuted.value;
  };

  const isGlobalAudioMuted = computed<boolean>(() => {
    return globalAudioOutputMuted.value;
  });

  /** sets local state to local mediastream and listens for device-changes */
  const initLocalMedia = async () => {
    await callTestStore.checkMic();
    await callTestStore.checkCam();

    if (!localStream.value && !streamInitialized.value) {
      streamInitialized.value = true;

      if (callTestStore.camSupported === CheckValue.CHECK_FAILED) {
        await setLocalVideoMutedState(true);
      }

      if (callTestStore.micSupported === CheckValue.CHECK_FAILED) {
        await setLocalAudioMutedState(true);
      }

      // load from local localStorage
      if (
        localStorage.getItem(LOCAL_VIDEO_MUTED) &&
        callTestStore.camSupported !== CheckValue.CHECK_FAILED
      ) {
        await setLocalVideoMutedState(localStorage.getItem(LOCAL_VIDEO_MUTED) === 'true');
      }

      if (
        localStorage.getItem(LOCAL_AUDIO_MUTED) &&
        callTestStore.micSupported !== CheckValue.CHECK_FAILED
      ) {
        await setLocalAudioMutedState(localStorage.getItem(LOCAL_AUDIO_MUTED) === 'true');
      }

      try {
        const stream = await navigator.mediaDevices.getUserMedia(inputConstraints.value);
        localStream.value = stream;
        mediaAccessFailed.value = false;

        // send blank video when no cam is allowed
        if (stream.getVideoTracks().length === 0) {
          const blankStream = createBlankVideoTrack();
          if (blankStream) {
            localStream.value.addTrack(blankStream.getVideoTracks()[0]);
          }
        }
      } catch (err) {
        logger.error({ msg: 'Error getting user media', error: err });
        mediaAccessFailed.value = true;
        void modalStore.notifyAndRun({
          ...CALL_HARD_ERRORS.media,
          // Read the router here, not at store setup: this store is first built
          // during boot from `main.ts` (via authStore → callStore), outside any
          // component setup, where `useRouter()`'s inject finds nothing — it
          // warned "injection Symbol(router) not found" and left `router`
          // undefined, so this push threw and the patient was never returned to
          // the waiting room.
          action: () => getRouter().push(getPath<'callWaitingRoom'>('/call/waiting-room', {})),
        });
      }

      const lastVideoInputId = localStorage.getItem(LAST_VIDEO_INPUT_ID);
      if (lastVideoInputId) {
        await setVideoId(lastVideoInputId);
      }

      const lastAudioInputId = localStorage.getItem(LAST_AUDIO_INPUT_ID);
      if (lastAudioInputId) {
        await setAudioInputId(lastAudioInputId);
      }

      const lastAudioOutputId = localStorage.getItem(LAST_AUDIO_OUTPUT_ID);
      if (lastAudioOutputId) {
        await setAudioOutputId(lastAudioOutputId);
      }

      if (browserName !== Browser.firefox) {
        selectedVideoInputId.value =
          localStream.value?.getVideoTracks()[0]?.getCapabilities().deviceId || 'default';

        selectedAudioInputId.value =
          localStream.value?.getAudioTracks()[0]?.getCapabilities().deviceId || 'default';
      }
    }

    await updateDevices({
      videoInputId: selectedVideoInputId.value,
      audioInputId: selectedAudioInputId.value,
      audioOutputId: selectedAudioOutputId.value,
    });

    if (!navigator.mediaDevices.ondevicechange) {
      navigator.mediaDevices.ondevicechange = () => {
        updateDevices({
          videoInputId: selectedVideoInputId.value,
          audioInputId: selectedAudioInputId.value,
          audioOutputId: selectedAudioOutputId.value,
        });
      };
    }

    /* if (audioOutputs.value.length > 0) {
      selectedAudioOutputId.value = audioOutputs.value[0].deviceId;
    } */
  };

  const updateMuteStates = () => {
    setLocalAudioMutedState(localAudioMuted.value);
    setLocalVideoMutedState(localVideoMuted.value);
  };

  /** sets and stores audio-mute state and sends it to all call-members */
  const setLocalAudioMutedState = async (localAudioMutedTmp: boolean) => {
    if (localStream.value?.getAudioTracks()) {
      localStream.value.getAudioTracks()[0].enabled = !localAudioMutedTmp;
    }
    dataConnectionsStore.broadcastP2pData({
      userId: authStore.authContext?.user.id || '',
      content: localAudioMutedTmp ? P2pSystemMsg.ownAudioOff : P2pSystemMsg.ownAudioOn,
      type: P2pDataType.system,
    });
    localStorage.setItem(LOCAL_AUDIO_MUTED, localAudioMutedTmp.toString());
    localAudioMuted.value = localAudioMutedTmp;
  };

  /** sets and stores video-mute state and sends it to all call-members */
  const setLocalVideoMutedState = async (localVideoMutedTmp: boolean) => {
    if (localStream.value?.getVideoTracks()) {
      localStream.value.getVideoTracks()[0].enabled = !localVideoMutedTmp;
    }
    dataConnectionsStore.broadcastP2pData({
      userId: authStore.authContext?.user.id || '',
      content: localVideoMutedTmp ? P2pSystemMsg.ownVideoOff : P2pSystemMsg.ownVideoOn,
      type: P2pDataType.system,
    });
    localStorage.setItem(LOCAL_VIDEO_MUTED, localVideoMutedTmp.toString());
    localVideoMuted.value = localVideoMutedTmp;
  };

  /**
   * Swaps the video track of localStream and of every outgoing p2p-mediaConnection.
   * The replaced track is stopped, so the device (cam or screen capture) is released.
   */
  const replaceLocalVideoTrack = (track: MediaStreamTrack) => {
    const oldTrack = localStream.value?.getVideoTracks()[0];
    if (oldTrack) {
      oldTrack.stop();
      localStream.value?.removeTrack(oldTrack);
    }
    localStream.value?.addTrack(track);

    mediaConnectionsStore.mediaConnections.forEach((call) => {
      const sender = call.peerConnection
        .getSenders()
        .find((sender) => sender.track?.kind === 'video');
      sender?.replaceTrack(track);
    });
  };

  /** updates local video-stream given a device-id */
  const setVideoId = async (videoDeviceId: string) => {
    inputConstraints.value.video = {
      deviceId: videoDeviceId ? { exact: videoDeviceId } : 'default',
    };
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { deviceId: videoDeviceId ? { exact: videoDeviceId } : 'default' },
        audio: false,
      });

      if (stream) {
        selectedVideoInputId.value = videoDeviceId || 'default';
        if (!screenShareEnabled.value) {
          replaceLocalVideoTrack(stream.getVideoTracks()[0]);

          // keep a muted cam muted across the swap, like `setAudioInputId` does
          stream.getVideoTracks()[0].enabled = !localVideoMuted.value;

          localStorage.setItem(LAST_VIDEO_INPUT_ID, videoDeviceId);
        }
      }
    } catch (err) {
      logger.error({ msg: 'no video stream', error: err });
    }
  };

  /** updates local video-stream given a device-id */
  const setAudioInputId = async (audioDeviceId: string) => {
    inputConstraints.value.audio = {
      deviceId: audioDeviceId ? { exact: audioDeviceId } : 'default',
    };
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { deviceId: audioDeviceId ? { exact: audioDeviceId } : 'default' },
        video: false,
      });
      if (stream) {
        selectedAudioInputId.value = audioDeviceId || 'default';
        localStream.value?.getAudioTracks()[0].stop();
        localStream.value?.removeTrack(localStream.value.getAudioTracks()[0]);
        localStream.value?.addTrack(stream.getAudioTracks()[0]);

        // mute/unmute audio
        if (localStream.value?.getAudioTracks()) {
          localStream.value.getAudioTracks()[0].enabled = !localAudioMuted.value;
        }

        localStorage.setItem(LAST_AUDIO_INPUT_ID, audioDeviceId);

        // replace tracks in outgoing p2p-mediaConnections
        mediaConnectionsStore.mediaConnections.forEach((call) => {
          const sender = call.peerConnection
            .getSenders()
            .find((sender) => sender.track?.kind === 'audio');
          sender?.replaceTrack(stream.getAudioTracks()[0]);
        });
      }
    } catch (err) {
      logger.error({ msg: 'no audio stream', error: err });
    }
  };

  const setAudioOutputId = async (audioId: string) => {
    selectedAudioOutputId.value = audioId;
    localStorage.setItem(LAST_AUDIO_OUTPUT_ID, audioId);

    if (!supportsAudioOutput) {
      logger.warn({
        msg: 'Audio output selection is not supported in this browser. Using default output.',
      });
      return;
    }

    // Get the media connections store to update remote audio elements
    const mediaConnectionsStore = useMediaConnectionsStore();
    if (
      mediaConnectionsStore.remoteAudioElements &&
      mediaConnectionsStore.remoteAudioElements.size > 0
    ) {
      for (const audioElement of mediaConnectionsStore.remoteAudioElements.values()) {
        try {
          await audioElement.setSinkId(audioId);
          logger.info({
            msg: 'Audio output device set for remote audio element: ' + audioId,
            audioId,
          });
        } catch (error) {
          logger.error({
            msg: 'Error setting sinkId on remote audio element' + audioId,
            error,
            audioId,
          });
        }
      }
    }
  };

  const updateDevices = async (options: {
    videoInputId: string;
    audioInputId: string;
    audioOutputId: string;
  }) => {
    let devices: MediaDeviceInfo[] = [];
    try {
      devices = await navigator.mediaDevices.enumerateDevices();

      videoDevices.value = devices.filter(
        (device) => device.kind === 'videoinput' && device.deviceId !== 'default',
      );
      audioInputs.value = devices.filter(
        (device) => device.kind === 'audioinput' && device.deviceId !== 'default',
      );
      audioOutputs.value = devices.filter(
        (device) => device.kind === 'audiooutput' && device.deviceId !== 'default',
      );
    } catch (error) {
      logger.error({ msg: 'Error enumerating media devices', error });
    }

    // fallback
    if (
      videoDevices.value.length > 0 &&
      !videoDevices.value.find((device) => device.deviceId === options.videoInputId)
    ) {
      setVideoId(videoDevices.value[0].deviceId);
    } else if (videoDevices.value.length === 0) {
      setVideoId('default');
    }

    if (
      audioInputs.value.length > 0 &&
      !audioInputs.value.find((device) => device.deviceId === options.audioInputId)
    ) {
      setAudioInputId(audioInputs.value[0].deviceId);
    } else if (audioInputs.value.length === 0) {
      setAudioInputId('default');
    }

    if (
      audioOutputs.value.length > 0 &&
      !audioOutputs.value.find((device) => device.deviceId === options.audioOutputId)
    ) {
      setAudioOutputId(audioOutputs.value[0].deviceId);
    } else if (audioOutputs.value.length === 0) {
      setAudioOutputId('default');
    }
  };

  /** replaces localStream with screen-share and sends it to all peers */
  const startScreenShare = async () => {
    const displayMediaOptions = {
      video: true,
      audio: false,
    };
    try {
      const stream = await navigator.mediaDevices.getDisplayMedia(displayMediaOptions);

      if (stream) {
        screenShareEnabled.value = true;
        screenShareStream.value = stream;
        replaceLocalVideoTrack(stream.getVideoTracks()[0]);

        // the browser's own "Stop sharing" bar ends the track without going through the toolbar
        stream.getVideoTracks()[0].onended = function () {
          stopScreenShare();
        };

        dataConnectionsStore.broadcastP2pData({
          userId: authStore.authContext?.user.id || '',
          content: P2pSystemMsg.focus,
          type: P2pDataType.system,
        });
      }
    } catch (err) {
      logger.error({ msg: 'Error starting screen share', error: err });
      modalStore.screenShareError = true;
    }
  };

  /** replaces localStream with cam-stream and sends it to all peers */
  const stopScreenShare = async () => {
    screenShareEnabled.value = false;

    // End the capture up front instead of relying on `setVideoId` to stop the old track:
    // if the cam cannot be reacquired (no cam, cam busy, stale device id) that call bails
    // out and the browser would keep sharing the screen with no way to stop it.
    screenShareStream.value?.getTracks().forEach((track) => track.stop());
    screenShareStream.value = undefined;

    await setVideoId(selectedVideoInputId.value);

    // No cam to fall back to - send a blank frame rather than leaving the peers with the
    // frozen last frame of the shared screen.
    const videoTrack = localStream.value?.getVideoTracks()[0];
    if (!videoTrack || videoTrack.readyState === 'ended') {
      const blankTrack = createBlankVideoTrack()?.getVideoTracks()[0];
      if (blankTrack) {
        replaceLocalVideoTrack(blankTrack);
      }
    }

    dataConnectionsStore.broadcastP2pData({
      userId: authStore.authContext?.user.id || '',
      content: P2pSystemMsg.focusEnd,
      type: P2pDataType.system,
    });
  };

  /** releases access to local cam and mic and updates state-variables */
  const stopLocalMedia = async () => {
    localStream.value?.getTracks().forEach((track) => {
      track.stop();
      track.enabled = false;
    });
    streamInitialized.value = false;
    localStream.value = undefined;

    // The store outlives a single call, so a hang-up while sharing must clear the
    // share state - otherwise the next call starts with the button showing "sharing".
    screenShareStream.value?.getTracks().forEach((track) => track.stop());
    screenShareStream.value = undefined;
    screenShareEnabled.value = false;
  };

  const createBlankVideoTrack = () => {
    const width = 1920;
    const height = 1080;

    const canvas = Object.assign(document.createElement('canvas'), {
      width,
      height,
    });

    const context = canvas.getContext('2d');
    if (context) {
      context.fillRect(0, 0, width, height);
      const stream = canvas.captureStream();
      return stream;
    }
  };

  //toggles
  const onVideoToggle = () => {
    setLocalVideoMutedState(!localVideoMuted.value);
  };

  const onAudioInputToggle = () => {
    setLocalAudioMutedState(!localAudioMuted.value);
  };

  const onScreenShare = () => {
    if (!screenShareEnabled.value) {
      startScreenShare();
    } else {
      stopScreenShare();
    }
  };

  return {
    onVideoToggle,
    onAudioInputToggle,
    onScreenShare,
    localStream,
    mediaAccessFailed,
    localAudioMuted,
    localVideoMuted,
    setLocalVideoMutedState,
    setLocalAudioMutedState,
    updateMuteStates,
    initLocalMedia,
    stopLocalMedia,
    screenShareEnabled,
    setVideoId,
    setAudioInputId,
    setAudioOutputId,
    videoDevices,
    audioInputs,
    audioOutputs,
    selectedAudioInputId,
    selectedAudioOutputId,
    selectedVideoInputId,
    startScreenShare,
    stopScreenShare,
    inputConstraints,
    toggleOutputMute,
    isGlobalAudioMuted,
  };
});
